import type { z } from 'zod';

import type { TokenStore } from './token-store';

export class ApiError extends Error {
  readonly status: number;
  readonly body?: unknown;
  /**
   * Identifiant stable du refus renvoyé par le backend, quand il en fournit
   * un. Sert à distinguer deux 401 qui n'appellent pas la même réaction — une
   * session expirée n'est pas un accès coupé par l'administrateur.
   */
  readonly code?: string;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = codeFrom(body);
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** Code d'erreur applicatif, quand la réponse en porte un. */
function codeFrom(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Schéma zod appliqué à la réponse. Sans schéma, la réponse est renvoyée telle quelle. */
  schema?: z.ZodType;
};

export type ApiClientConfig = {
  baseUrl: string;
  tokens: TokenStore;
  /**
   * Appelé sur 401 : l'app décide de rediriger vers /login. `code` porte la
   * raison quand le backend en donne une, pour afficher autre chose qu'un
   * « session expirée » générique.
   */
  onUnauthorized?: (code?: string) => void;
};

export type ApiClient = ReturnType<typeof createApiClient>;

export function createApiClient({ baseUrl, tokens, onUnauthorized }: ApiClientConfig) {
  async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, signal, headers = {}, schema } = options;
    const token = tokens.get();

    const response = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    const payload = await parseBody(response);

    if (!response.ok) {
      // 403 = authentifié mais pas autorisé (ex. compte secondaire sur une
      // route d'admin) : le jeton reste valide, on ne déconnecte pas.
      const error = new ApiError(response.status, messageFrom(payload, response), payload);
      if (response.status === 401) {
        tokens.clear();
        onUnauthorized?.(error.code);
      }
      throw error;
    }

    const data = unwrap(payload);
    if (!schema) return data as T;

    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiError(response.status, `Réponse inattendue pour ${path}`, parsed.error.issues);
    }
    return parsed.data as T;
  }

  return {
    request,
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'POST', body }),
    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'PATCH', body }),
    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'PUT', body }),
    delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'DELETE' }),
    tokens,
  };
}

/**
 * Le backend enveloppe tout dans `{ success, data }`. On rend `data` seul aux
 * appelants : les schémas décrivent la charge utile, pas l'enveloppe. Une
 * réponse sans enveloppe (santé, proxy) passe telle quelle.
 */
function unwrap(payload: unknown): unknown {
  if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function messageFrom(payload: unknown, response: Response): string {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const { message } = payload as { message: unknown };
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
  }
  if (typeof payload === 'string' && payload) return payload;
  return `${response.status} ${response.statusText}`;
}
